import React, { useContext, useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../../images/logo.jpg"; // Adjusted path
import { AuthContext } from "../../context/AuthContext"; // Adjusted path
import "./Header.css";

function Header() {
  const navigate = useNavigate();
  const { isLoggedIn, userRole, setIsLoggedIn, setUserRole } =
    useContext(AuthContext);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadDMs, setUnreadDMs] = useState(0);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleDashboardClick = () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData && userData.role === "professor") {
      navigate("/professor-dashboard");
    } else if (userData && userData.role === "student") {
      navigate("/student-dashboard");
    } else {
      navigate("/");
    }
  };

  const handleMatchingClick = () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData && userData.role === "professor") {
      navigate("/professor-matching");
    } else if (userData && userData.role === "student") {
      navigate("/student-matching");
    } else {
      navigate("/");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setUserRole(null);
    navigate("/");
    setShowProfileDropdown(false);
    setShowMobileMenu(false);
  };

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user")) || {};
        const token = userData.token;

        if (!token) return;

        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUnreadNotifications(res.data.unreadCount || 0);
      } catch (err) {
        console.error("Error fetching notification count:", err);
      }
    };

    if (isLoggedIn) {
      fetchUnreadCount();
      // Refresh notification count every 5 seconds
      const interval = setInterval(fetchUnreadCount, 5000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  // Fetch unread direct message count
  useEffect(() => {
    const fetchUnreadDMs = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user")) || {};
        const token = userData.token;
        if (!token) return;

        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/unread-count`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUnreadDMs(res.data.unreadCount || 0);
      } catch (err) {
        console.error("Error fetching DM unread count:", err);
      }
    };

    if (isLoggedIn) {
      fetchUnreadDMs();
      const interval = setInterval(fetchUnreadDMs, 4000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  // Listen for optimistic updates from DM page
  useEffect(() => {
    const handler = (event) => {
      const { delta, absolute } = event.detail || {};
      setUnreadDMs((prev) => {
        if (typeof absolute === "number") return absolute;
        if (typeof delta === "number") return Math.max(0, prev + delta);
        return prev;
      });
    };

    window.addEventListener("dm-unread-updated", handler);
    return () => window.removeEventListener("dm-unread-updated", handler);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      // Add this check to exclude the mobile menu button
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest(".mobile-menu-btn")
      ) {
        setShowMobileMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const userName = userData.name || "User Name";
  const userEmail = userData.email || "user@example.com";

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="logo-link">
          <img src={logo} alt="NeuHawk Logo" className="header_logo" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <Link to="/blog" className="nav-link">
            Blog
          </Link>
          <Link to="/welcome-page" className="nav-link">
            About
          </Link>
          <Link to="/Contact" className="nav-link">
            Contact
          </Link>

          {isLoggedIn&& (
            <Link
              to="/direct-messages"
              className="dm-link"
              title="Direct Messages"
            >
              <svg 
                className="dm-icon" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                >
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 6l-7 4.5L5 9V5h14v4z" />
              </svg>
              {unreadDMs > 0 && (
                <span className="dm-badge">
                  {unreadDMs > 99 ? "99+" : unreadDMs}
                </span>
              )}
            </Link>
          )}

          {/* Notification Bell Icon */}
          {isLoggedIn && (
            <Link
              to="/notifications"
              className="notification-link"
              title="Notifications"
            >
              <svg
                className="notification-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
              </svg>
              {unreadNotifications > 0 && (
                <span className="notification-badge">
                  {unreadNotifications > 99 ? "99+" : unreadNotifications}
                </span>
              )}
            </Link>
          )}

          {/* Profile Icon with Dropdown */}
          {isLoggedIn && (
            <div className="profile-container" ref={dropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="profile-button"
              >
                <svg
                  className="profile-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <div className="dropdown-menu">
                  {/* Account Header */}
                  <div className="dropdown-header">
                    <p className="account-label">Account</p>
                    <div className="user-info">
                      <div className="user-avatar">
                        <svg
                          className="avatar-icon"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                        </svg>
                      </div>
                      <div className="user-details">
                        <p className="user-name">{userName}</p>
                        <p className="user-email">{userEmail}</p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="dropdown-items">
                    <Link
                      to="/profile"
                      onClick={() => setShowProfileDropdown(false)}
                      className="dropdown-item"
                    >
                      <svg
                        className="item-icon"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                      My Profile
                    </Link>
                    <Link
                      to="/account-settings"
                      onClick={() => setShowProfileDropdown(false)}
                      className="dropdown-item"
                    >
                      <svg className="item-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                      </svg>
                      Account / Billing
                    </Link>
                    <Link
                      to="/notifications"
                      onClick={() => setShowProfileDropdown(false)}
                      className="dropdown-item"
                    >
                      <svg className="item-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21,19V20H3V19L5,17V11C5,7.9 7.03,5.17 10,4.29C10,4.19 10,4.1 10,4A2,2 0 0,1 12,2A2,2 0 0,1 14,4C14,4.1 14,4.19 14,4.29C16.97,5.17 19,7.9 19,11V17L21,19M14,21A2,2 0 0,1 12,23A2,2 0 0,1 10,21"/>
                      </svg>
                      Notification settings
                    </Link>
                    <hr className="dropdown-divider" />
                    <button
                      onClick={handleLogout}
                      className="dropdown-item logout-btn"
                    >
                      <svg
                        className="item-icon"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z" />
                      </svg>
                      Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isLoggedIn && (
            <div className="auth-buttons">
              <Link to="/role-selection" className="btn btn-register">
                Register
              </Link>
              <Link to="/login" className="btn btn-login">
                Log In
              </Link>
            </div>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="mobile-menu-btn"
        >
          <svg className="menu-icon" viewBox="0 0 24 24" fill="currentColor">
            {showMobileMenu ? (
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            ) : (
              <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div ref={mobileMenuRef} className="mobile-menu">
          <div className="mobile-menu-content">
            <Link
              to="/blog"
              onClick={() => setShowMobileMenu(false)}
              className="mobile-nav-link"
            >
              Blog
            </Link>
            <Link
              to="/welcome-page"
              onClick={() => setShowMobileMenu(false)}
              className="mobile-nav-link"
            >
              About
            </Link>
            <Link
              to="/Contact"
              onClick={() => setShowMobileMenu(false)}
              className="mobile-nav-link"
            >
              Contact
            </Link>

            {isLoggedIn ? (
              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <div className="mobile-user-avatar">
                    <svg
                      className="mobile-avatar-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                  <div className="mobile-user-details">
                    <p className="mobile-user-name">{userName}</p>
                    <p className="mobile-user-email">{userEmail}</p>
                  </div>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setShowMobileMenu(false)}
                  className="mobile-menu-item"
                >
                  My Profile
                </Link>
                <Link
                  to="/account-settings"
                  onClick={() => setShowMobileMenu(false)}
                  className="mobile-menu-item"
                >
                  Account / Billing
                </Link>
                <Link 
                  to="/direct-messages"
                  onClick={() => setShowMobileMenu(false)}
                  className="mobile-menu-item"
                >
                  Direct Messages
                </Link>
                <Link
                  to="/notifications"
                  onClick={() => setShowMobileMenu(false)}
                  className="mobile-menu-item"
                >
                  Notifications
                  {unreadNotifications > 0 && (
                    <span className="mobile-notification-badge">
                      {unreadNotifications > 99 ? "99+" : unreadNotifications}
                    </span>
                  )}
                </Link>
                <button
                  onClick={handleLogout}
                  className="mobile-menu-item mobile-logout-btn"
                >
                  Log Out
                </button>
              </div>
            ) : (
              <div className="mobile-auth-buttons">
                <Link
                  to="/role-selection"
                  onClick={() => setShowMobileMenu(false)}
                  className="btn btn-register mobile-btn"
                >
                  Register
                </Link>
                <Link
                  to="/login"
                  onClick={() => setShowMobileMenu(false)}
                  className="btn btn-login mobile-btn"
                >
                  Log In
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
